import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeDropper } from "@fortawesome/free-solid-svg-icons";
import ColorPicker from "../../../Component/ColorPicker/ColorPicker";
import useFetch from "../../../Component/Hooks/useFetch";

const EditWidgetCatalogue = ({ widgetId }) => {
  const user = useSelector((state) => state.userReducer);
  const userId = user.user._id;

  const [isLoading, setIsLoading] = useState(false);
  const [widgetData, setWidgetData] = useState({
    isOnline: "online",
    type: "Widget Catalogue",
  });
  const [categories, setCategories] = useState([]);

  const { loading, data } = useFetch(
    `${process.env.REACT_APP_API_URL}/api/widgets/catalogue/getOneWidget/${widgetId}`
  );

  useEffect(() => {
    if (data) {
      setWidgetData(data.widget);
      setCategories(data.widget.categories || []);
    }
  }, [data, widgetId]);

  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [colorPickers, setColorPickers] = useState({
    backgroundColor: false,
    btnColor: false,
    labelColor: false,
    borderColor: false,
  });

  const handleInputChange = (e) => {
    const value =
      e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setWidgetData({ ...widgetData, [e.target.name]: value });
  };

  const handleColorPickerToggle = (fieldName) => {
    setColorPickers((prevPickers) => ({
      ...Object.keys(prevPickers).reduce((acc, key) => {
        acc[key] = key === fieldName ? !prevPickers[key] : false;
        return acc;
      }, {}),
    }));
    setActiveColorPicker(fieldName);
  };

  const handleColorChange = (color) => {
    setWidgetData((prevData) => ({
      ...prevData,
      [activeColorPicker]: color.hex,
    }));
  };

  /* ── Category helpers ── */
  const addCategory = () => {
    setCategories([
      ...categories,
      { name: "", order: categories.length, prestations: [] },
    ]);
  };

  const removeCategory = (catIndex) => {
    setCategories(categories.filter((_, i) => i !== catIndex));
  };

  const handleCategoryChange = (catIndex, value) => {
    const updated = categories.map((cat, i) =>
      i === catIndex ? { ...cat, name: value } : cat
    );
    setCategories(updated);
  };

  /* ── Prestation helpers ── */
  const addPrestation = (catIndex) => {
    const updated = categories.map((cat, i) => {
      if (i !== catIndex) return cat;
      return {
        ...cat,
        prestations: [
          ...(cat.prestations || []),
          {
            name: "",
            description: "",
            price: 0,
            duration: 0,
            quantityEnabled: false,
            order: (cat.prestations || []).length,
          },
        ],
      };
    });
    setCategories(updated);
  };

  const removePrestation = (catIndex, prestaIndex) => {
    const updated = categories.map((cat, i) => {
      if (i !== catIndex) return cat;
      return {
        ...cat,
        prestations: (cat.prestations || []).filter(
          (_, pi) => pi !== prestaIndex
        ),
      };
    });
    setCategories(updated);
  };

  const handlePrestationChange = (catIndex, prestaIndex, field, value) => {
    const updated = categories.map((cat, i) => {
      if (i !== catIndex) return cat;
      const updatedPrestas = (cat.prestations || []).map((presta, pi) =>
        pi === prestaIndex ? { ...presta, [field]: value } : presta
      );
      return { ...cat, prestations: updatedPrestas };
    });
    setCategories(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const categoriesWithOrder = categories.map((cat, catIndex) => ({
      ...cat,
      order: catIndex,
      prestations: (cat.prestations || []).map((presta, prestaIndex) => ({
        ...presta,
        order: prestaIndex,
      })),
    }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/widgets/catalogue/edite/${widgetId}/user/${userId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...widgetData,
            priceActive:
              widgetData.priceActive === "true" ||
              widgetData.priceActive === true,
            categories: categoriesWithOrder,
          }),
        }
      );
      if (response.ok) {
        toast.success("Widget édité avec succès");
      } else {
        switch (response.status) {
          case 401:
            toast.error("Vous n'êtes pas connecté");
            break;
          case 400:
            toast.error("Attention, un widget existe déjà avec ce nom.");
            break;
          default:
            toast.error("Une erreur est survenue");
            break;
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Une erreur est survenue lors de la mise à jour.");
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    "text-center block w-full rounded-lg border border-gray-200 p-2.5 text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all";

  return (
    <div className="w-full max-w-2xl mx-auto py-8 px-4">
      {loading ? (
        <p className="text-center text-gray-400">Chargement...</p>
      ) : user.user.isPremium ? (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">

          {/* Titre */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Titre du widget *
            </label>
            <input
              className={inputClass}
              type="text"
              name="title"
              placeholder="Ex : Catalogue Prestations - Mon Entreprise"
              value={widgetData.title || ""}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Sous-titre */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Sous-titre
            </label>
            <input
              className={inputClass}
              type="text"
              name="subtitle"
              placeholder="Ex : Choisissez vos prestations"
              value={widgetData.subtitle || ""}
              onChange={handleInputChange}
            />
          </div>

          {/* Afficher le prix */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Afficher le prix au client ?
            </label>
            <select
              className={inputClass}
              name="priceActive"
              value={widgetData.priceActive}
              onChange={handleInputChange}
            >
              <option value="true">Oui</option>
              <option value="false">Non</option>
            </select>
          </div>

          {/* Statut en ligne */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Statut
            </label>
            <select
              className={inputClass}
              name="isOnline"
              value={widgetData.isOnline || "online"}
              onChange={handleInputChange}
            >
              <option value="online">En ligne</option>
              <option value="offline">Hors ligne</option>
            </select>
          </div>

          {/* Catégories / Prestations */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Catalogue / Prestations
            </p>

            <div className="flex flex-col gap-4">
              {categories.map((cat, catIndex) => (
                <div
                  key={catIndex}
                  className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-3"
                >
                  {/* Category header */}
                  <div className="flex items-center gap-2">
                    <input
                      className="flex-1 rounded-lg border border-gray-200 p-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      type="text"
                      placeholder="Nom de la catégorie"
                      value={cat.name}
                      onChange={(e) =>
                        handleCategoryChange(catIndex, e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="w-8 h-8 shrink-0 rounded-lg bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors"
                      onClick={() => removeCategory(catIndex)}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Prestations */}
                  <div className="flex flex-col gap-2 pl-2 border-l-2 border-gray-100">
                    {(cat.prestations || []).map((presta, prestaIndex) => (
                      <div
                        key={prestaIndex}
                        className="bg-gray-50 border border-gray-100 rounded-lg p-3 flex flex-col gap-2"
                      >
                        <div className="flex items-center gap-2">
                          <input
                            className="flex-1 rounded-lg border border-gray-200 p-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            type="text"
                            placeholder="Nom de la prestation"
                            value={presta.name}
                            onChange={(e) =>
                              handlePrestationChange(
                                catIndex,
                                prestaIndex,
                                "name",
                                e.target.value
                              )
                            }
                          />
                          <button
                            type="button"
                            className="w-7 h-7 shrink-0 rounded-lg bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors"
                            onClick={() =>
                              removePrestation(catIndex, prestaIndex)
                            }
                          >
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2.5}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>

                        <input
                          className="rounded-lg border border-gray-200 p-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                          type="text"
                          placeholder="Description (optionnel)"
                          value={presta.description || ""}
                          onChange={(e) =>
                            handlePrestationChange(
                              catIndex,
                              prestaIndex,
                              "description",
                              e.target.value
                            )
                          }
                        />

                        <div className="grid grid-cols-2 gap-2">
                          <div className="relative">
                            <input
                              className="w-full rounded-lg border border-gray-200 p-2 pr-7 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                              type="number"
                              placeholder="Prix"
                              value={presta.price || 0}
                              onChange={(e) =>
                                handlePrestationChange(
                                  catIndex,
                                  prestaIndex,
                                  "price",
                                  Number(e.target.value)
                                )
                              }
                            />
                            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                              €
                            </span>
                          </div>
                          <div className="relative">
                            <input
                              className="w-full rounded-lg border border-gray-200 p-2 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                              type="number"
                              placeholder="Durée"
                              value={presta.duration || 0}
                              onChange={(e) =>
                                handlePrestationChange(
                                  catIndex,
                                  prestaIndex,
                                  "duration",
                                  Number(e.target.value)
                                )
                              }
                            />
                            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                              min
                            </span>
                          </div>
                        </div>

                        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={presta.quantityEnabled || false}
                            onChange={(e) =>
                              handlePrestationChange(
                                catIndex,
                                prestaIndex,
                                "quantityEnabled",
                                e.target.checked
                              )
                            }
                            className="rounded border-gray-300 focus:ring-gray-900"
                          />
                          Permettre la sélection de quantité
                        </label>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="w-full p-2 rounded-lg text-xs font-medium border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-all"
                      onClick={() => addPrestation(catIndex)}
                    >
                      + Ajouter une prestation
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="w-full p-2.5 rounded-lg text-sm font-medium border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-all"
              onClick={addCategory}
            >
              + Ajouter une catégorie
            </button>
          </div>

          {/* Apparence */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Apparence
            </p>
            <div className="-mx-3 flex flex-wrap">
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur de fond"
                  color={widgetData.backgroundColor || ""}
                  onColorChange={(color) => handleColorChange(color)}
                  isOpen={colorPickers.backgroundColor}
                  togglePicker={() => handleColorPickerToggle("backgroundColor")}
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur des boutons"
                  color={widgetData.btnColor || ""}
                  onColorChange={(color) => handleColorChange(color)}
                  isOpen={colorPickers.btnColor}
                  togglePicker={() => handleColorPickerToggle("btnColor")}
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur du texte"
                  color={widgetData.labelColor || ""}
                  onColorChange={(color) => handleColorChange(color)}
                  isOpen={colorPickers.labelColor}
                  togglePicker={() => handleColorPickerToggle("labelColor")}
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
            </div>
            <div className="-mx-3 flex flex-wrap mt-1">
              <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                <ColorPicker
                  label="Couleur de bordure"
                  color={widgetData.borderColor || ""}
                  onColorChange={(color) => handleColorChange(color)}
                  isOpen={colorPickers.borderColor}
                  togglePicker={() => handleColorPickerToggle("borderColor")}
                  placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Contact
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Email
                </label>
                <input
                  className={inputClass}
                  type="email"
                  name="contactMail"
                  placeholder="contact@entreprise.fr"
                  value={widgetData.contactMail || ""}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Téléphone
                </label>
                <input
                  className={inputClass}
                  type="tel"
                  name="contactPhone"
                  placeholder="06 12 34 56 78"
                  value={widgetData.contactPhone || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="w-full p-3 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: widgetData.btnColor || "#000" }}
          >
            {isLoading ? "Chargement..." : "Enregistrer les modifications"}
          </button>
        </form>
      ) : null}
    </div>
  );
};

export default EditWidgetCatalogue;
